import { api } from './api';

export interface UploadedAttachment {
  id: string;
  name: string;
  mime: string;
  size: number;
  url: string;
  width?: number;
  height?: number;
  durationMs?: number;
}

export interface UploadProgress {
  fileId: string;
  name: string;
  size: number;
  loaded: number;
  status: 'preparing' | 'uploading' | 'finalizing' | 'done' | 'error';
  error?: string;
}

/**
 * Drive a single file through sign → PUT → complete.
 *
 * We use XHR (not fetch) because fetch still doesn't have upload progress
 * events in 2026 — it's been "proposal" for years. XHR isn't pretty but
 * it gives us per-byte progress so the UI can render a real progress bar
 * instead of a spinner.
 *
 * The caller is responsible for showing UI and cancellation.
 */
export async function uploadFile(
  file: File,
  opts: {
    fileId: string;
    onProgress?: (p: UploadProgress) => void;
    signal?: AbortSignal;
    /** Caller-known media duration (ms). Voice notes pass the value measured
     *  by the recorder, since a fresh MediaRecorder WebM blob reports
     *  `duration === Infinity` until it's fully seeked. Takes precedence over
     *  the best-effort probe. */
    durationMs?: number;
  },
): Promise<UploadedAttachment> {
  const { fileId, onProgress, signal } = opts;
  const emit = (patch: Partial<UploadProgress>) => {
    onProgress?.({
      fileId,
      name: file.name,
      size: file.size,
      loaded: 0,
      status: 'preparing',
      ...patch,
    });
  };

  try {
    emit({ status: 'preparing' });
    const presigned = await api.signUpload({
      name: file.name,
      size: file.size,
      mime: file.type || 'application/octet-stream',
    });
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');

    emit({ status: 'uploading' });
    await putWithProgress({
      url: presigned.uploadUrl,
      file,
      mime: presigned.mime,
      onProgress: (loaded) =>
        emit({ status: 'uploading', loaded }),
      signal,
    });

    // Best-effort dimension capture for images/video so the renderer can
    // reserve space instead of jumping when the resource decodes.
    const dim = await probeMedia(file);

    // Prefer the caller-supplied duration (voice notes). Guard against
    // non-finite values — a stray Infinity/NaN serialises to JSON `null`,
    // which the server's `.optional()` schema rejects (→ 500).
    const durationCandidate = opts.durationMs ?? dim.durationMs;
    const durationMs =
      typeof durationCandidate === 'number' && Number.isFinite(durationCandidate) && durationCandidate > 0
        ? Math.round(durationCandidate)
        : undefined;

    emit({ status: 'finalizing', loaded: file.size });
    const completed = await api.completeUpload({
      key: presigned.key,
      name: presigned.name,
      mime: presigned.mime,
      width: dim.width,
      height: dim.height,
      durationMs,
    });

    emit({ status: 'done', loaded: file.size });
    return completed;
  } catch (err) {
    if ((err as Error).name !== 'AbortError') {
      emit({ status: 'error', error: (err as Error).message });
    }
    throw err;
  }
}

function putWithProgress(input: {
  url: string;
  file: File;
  mime: string;
  onProgress?: (loaded: number) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', input.url, true);
    xhr.setRequestHeader('Content-Type', input.mime);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) input.onProgress?.(e.loaded);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`s3_put_failed_${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('s3_put_network')));
    xhr.addEventListener('abort', () => reject(new DOMException('aborted', 'AbortError')));
    input.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(input.file);
  });
}

/**
 * Read intrinsic dimensions / duration from an image, video or audio file.
 * Best-effort: returns an empty object on anything we can't decode.
 */
async function probeMedia(file: File): Promise<{
  width?: number;
  height?: number;
  durationMs?: number;
}> {
  if (file.type.startsWith('image/')) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const out = { width: img.naturalWidth, height: img.naturalHeight };
        URL.revokeObjectURL(url);
        resolve(out);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({});
      };
      img.src = url;
    });
  }
  if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const media =
        file.type.startsWith('video/')
          ? document.createElement('video')
          : document.createElement('audio');
      media.preload = 'metadata';
      media.onloadedmetadata = () => {
        const ms = Math.round(media.duration * 1000);
        const out: { width?: number; height?: number; durationMs?: number } = {};
        // MediaRecorder WebM blobs often report Infinity/NaN duration until
        // seeked — never forward those (they JSON-serialise to null and 500
        // the upload-complete schema). The composer passes the real value.
        if (Number.isFinite(ms) && ms > 0) out.durationMs = ms;
        if ('videoWidth' in media) {
          out.width = (media as HTMLVideoElement).videoWidth;
          out.height = (media as HTMLVideoElement).videoHeight;
        }
        URL.revokeObjectURL(url);
        resolve(out);
      };
      media.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({});
      };
      media.src = url;
    });
  }
  return {};
}
